import { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { Message } from '@/types/message';
import PaginatedResponse from '@/types/paginatedResponse';
import { PaginationParams } from '@/types/pagination';
import ajax from '@/utils/ajax';
import { errorMessage } from '@/utils/mapAxiosErrorMessages';
export interface MessagesParams extends PaginationParams {
  [key: string]: string;
}

export const messageRepository = {
  async getMessage({
    host,
    messageId
  }: {
    host: string;
    messageId: string;
  }): Promise<Message | void> {
    try {
      const path = `/api/dugout/messages/${messageId}/`;
      const { data } = await ajax.get({
        url: `${host}${path}`
      });

      return data;
    } catch (error) {
      await Promise.reject(`Failed to get message. ${errorMessage(error as AxiosError)}`);
    }
  },

  // Fetch messages with a query params that
  // includes the personId
  async getMessages({
    host,
    params
  }: {
    host: string;
    params: MessagesParams;
  }): Promise<PaginatedResponse<Message[]>> {
    const path = `/api/dugout/messages/`;
    const { data } = await ajax.get({
      url: `${host}${path}`,
      params
    });

    return data;
  },
  async send({
    host,
    personId,
    content,
    channel = 'sms',
    replyToMessageId,
    sender
  }: {
    host: string;
    personId: string;
    content: string;
    channel?: string;
    replyToMessageId?: string;
    sender?: string;
  }): Promise<Message> {
    const path = `/api/dugout/messages/send_message/`;

    const response = await ajax.post<
      {
        person_id: string;
        content: string;
        channel: string;
        reply_to_message_id?: string;
        sender?: string;
      },
      AxiosResponse
    >({
      url: `${host}${path}`,
      data: {
        person_id: personId,
        content,
        channel,
        ...(replyToMessageId ? { reply_to_message_id: replyToMessageId } : {}),
        ...(sender ? { sender } : {})
      }
    });
    return response.data;
  },
  async sendEmail({
    host,
    personId,
    subject,
    content,
    sender,
    replyToMessageId,
    draftId,
    attachments
  }: {
    host: string;
    personId: string;
    subject: string;
    content: string;
    sender?: string;
    replyToMessageId?: string;
    draftId?: string | null;
    attachments?: { id: string; name: string; size: number }[];
  }): Promise<{ status: string }> {
    const path = `/api/dugout/messages/send_email/`;

    const response = await ajax.post<
      {
        person_id: string;
        subject: string;
        content: string;
        sender?: string;
        reply_to_message_id?: string;
        draft_id?: string;
        attachments?: { id: string; name: string; size: number }[];
      },
      AxiosResponse
    >({
      url: `${host}${path}`,
      data: {
        person_id: personId,
        subject,
        content,
        ...(sender ? { sender } : {}),
        ...(replyToMessageId ? { reply_to_message_id: replyToMessageId } : {}),
        ...(draftId ? { draft_id: draftId } : {}),
        ...(attachments?.length ? { attachments } : {})
      }
    });
    return response.data;
  },

  async prepareAttachment({
    host,
    personId,
    name,
    contentType,
    size,
    draftId
  }: {
    host: string;
    personId: string;
    name: string;
    contentType: string;
    size: number;
    draftId?: string | null;
  }): Promise<{
    draft_id: string;
    attachment_id: string;
    upload_url: string;
    name: string;
    size: number;
  }> {
    const response = await ajax.post<object, AxiosResponse>({
      url: `${host}/api/dugout/messages/prepare_attachment/`,
      data: {
        person_id: personId,
        name,
        content_type: contentType,
        size,
        ...(draftId ? { draft_id: draftId } : {})
      }
    });
    return response.data;
  },

  async removeAttachment({
    host,
    draftId,
    attachmentId,
    personId
  }: {
    host: string;
    draftId: string;
    attachmentId: string;
    personId: string;
  }): Promise<void> {
    await ajax.delete<void, AxiosResponse>({
      url: `${host}/api/dugout/messages/attachments/${attachmentId}/`,
      config: { params: { draft_id: draftId, person_id: personId } }
    });
  },

  async getAttachmentDownloadUrl({
    host,
    attachmentId,
    kustomerMessageId,
    personId
  }: {
    host: string;
    attachmentId: string;
    kustomerMessageId: string;
    personId: string;
  }): Promise<{ url: string }> {
    const { data } = await ajax.get({
      url: `${host}/api/dugout/messages/attachments/${attachmentId}/download/`,
      params: { kustomer_message_id: kustomerMessageId, person_id: personId }
    });
    return data as { url: string };
  },

  async kustomerConversationId({
    host,
    messageId
  }: {
    host: string;
    messageId: string;
  }): Promise<{ kustomer_id: string } | void> {
    const path = `/api/dugout/messages/${messageId}/kustomer_conversation_id/`;
    const { data } = await ajax.get({
      url: `${host}${path}`
    });

    return data;
  },

  async getSenders({
    host
  }: {
    host: string;
  }): Promise<{ channel: string; sender: string; name?: string }[]> {
    const { data } = await ajax.get({ url: `${host}/api/dugout/messages/senders/` });
    return data;
  }
};

const MessageRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getMessage(messageId: string): Promise<Message | void> {
      return messageRepository.getMessage({ host, messageId });
    },
    async getMessages(params: MessagesParams): Promise<PaginatedResponse<Message[]>> {
      return messageRepository.getMessages({ host, params });
    },
    async send({
      personId,
      content,
      channel = 'sms',
      replyToMessageId,
      sender
    }: {
      personId: string;
      content: string;
      channel?: string;
      replyToMessageId?: string;
      sender?: string;
    }): Promise<Message> {
      return messageRepository.send({
        host,
        personId,
        content,
        channel,
        replyToMessageId,
        sender
      });
    },
    async sendEmail({
      personId,
      subject,
      content,
      sender,
      replyToMessageId,
      draftId,
      attachments
    }: {
      personId: string;
      subject: string;
      content: string;
      sender?: string;
      replyToMessageId?: string;
      draftId?: string | null;
      attachments?: { id: string; name: string; size: number }[];
    }): Promise<{ status: string }> {
      return messageRepository.sendEmail({
        host,
        personId,
        subject,
        content,
        sender,
        replyToMessageId,
        draftId,
        attachments
      });
    },

    async prepareAttachment({
      personId,
      file,
      draftId
    }: {
      personId: string;
      file: File;
      draftId?: string | null;
    }): Promise<{
      draft_id: string;
      attachment_id: string;
      upload_url: string;
      name: string;
      size: number;
    }> {
      return messageRepository.prepareAttachment({
        host,
        personId,
        name: file.name,
        contentType: file.type,
        size: file.size,
        draftId
      });
    },

    async removeAttachment({
      draftId,
      attachmentId,
      personId
    }: {
      draftId: string;
      attachmentId: string;
      personId: string;
    }): Promise<void> {
      return messageRepository.removeAttachment({ host, draftId, attachmentId, personId });
    },

    async getAttachmentDownloadUrl(
      attachmentId: string,
      kustomerMessageId: string,
      personId: string
    ): Promise<{ url: string }> {
      return messageRepository.getAttachmentDownloadUrl({
        host,
        attachmentId,
        kustomerMessageId,
        personId
      });
    },

    async kustomerConversationId(messageId: string): Promise<{ kustomer_id: string } | void> {
      return messageRepository.kustomerConversationId({ host, messageId });
    },

    async getSenders(): Promise<{ channel: string; sender: string; name?: string }[]> {
      return messageRepository.getSenders({ host });
    }
  };
};

export default MessageRepository;
